/**
 * @file PlanTypeImageTab.js
 * @description Image tab inside the Display Properties modal.
 */

import { useState, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Popover, Box } from '@mui/material';
import NotFoundBox from '../../ui/no_data_indicators/NotFoundBox';
import { ModalContext, ModalTypes } from '../../../store/ModalContext';
import { getDefaultBenefitImageByPlanCode } from '../../../utils/benefitPlanImageDefaults';
import styles from '../PlanTypeDisplayProperties.module.scss';
import { getText } from '../../../i18n';

const PlanTypeImageTab = ({
    viewModel,
    draft,
    onChange,
    onUploadingChange,
}) => {
    const { showModal } = useContext(ModalContext);
    const [anchorEl, setAnchorEl] = useState(null);
    const [addPopoverOpen, setAddPopoverOpen] = useState(false);
    const [didAutoLoadDefaultImage, setDidAutoLoadDefaultImage] =
        useState(false);

    const addPopoverId = addPopoverOpen ? 'plan-type-image-popover' : undefined;
    const currentUrl = draft?.imageUrl ?? null;
    const isSystemPlanType =
        viewModel?.isSystemGenerated === true ||
        viewModel?.planType?.tenantId == null;
    const planCode =
        draft?.codeOverride ||
        viewModel?.code ||
        viewModel?.planType?.code ||
        '';
    const defaultSystemPlanImage = useMemo(
        () => getDefaultBenefitImageByPlanCode(planCode),
        [planCode],
    );

    useEffect(() => {
        setDidAutoLoadDefaultImage(false);
    }, [viewModel?.id, viewModel?.planTypeId, viewModel?.planType?.id]);

    useEffect(() => {
        if (didAutoLoadDefaultImage) {
            return;
        }

        if (!isSystemPlanType) {
            setDidAutoLoadDefaultImage(true);
            return;
        }

        if (currentUrl) {
            setDidAutoLoadDefaultImage(true);
            return;
        }

        if (!defaultSystemPlanImage) {
            setDidAutoLoadDefaultImage(true);
            return;
        }

        onChange({ imageUrl: defaultSystemPlanImage, imageAssetId: null });
        setDidAutoLoadDefaultImage(true);
    }, [
        currentUrl,
        defaultSystemPlanImage,
        didAutoLoadDefaultImage,
        isSystemPlanType,
        onChange,
    ]);

    const handleAddButtonClick = event => {
        setAnchorEl(event.currentTarget);
        setAddPopoverOpen(prev => !prev);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
        setAddPopoverOpen(false);
    };

    const handleUploadFromDevice = () => {
        handlePopoverClose();
        showModal({
            type: ModalTypes.UPLOAD_FROM_DEVICE,
            title: getText('msg_dcf4cc69bca4'),
            subtitle: getText('msg_321a6dc46018'),
            onYes: uploadedAsset => {
                onChange({
                    imageUrl: uploadedAsset.url,
                    imageAssetId: uploadedAsset.id,
                });
            },
            onNo: () => {},
            onHide: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_8bdf057f91e7'),
        });
    };

    const handleAddFromLibrary = () => {
        handlePopoverClose();
        showModal({
            type: ModalTypes.ADD_FROM_ASSETS,
            title: getText('msg_0bc609667aa7'),
            onYes: selectedAsset => {
                onChange({
                    imageUrl: selectedAsset.url,
                    imageAssetId: selectedAsset.id,
                });
            },
            onNo: () => {},
            onHide: () => {},
            noButtonText: getText('msg_77dfd2135f4d'),
            yesButtonText: getText('msg_61cc55aa0453'),
            acceptedFileTypes: ['jpg', 'jpeg', 'png', 'svg', 'webp', 'gif'],
            assetTypes: [],
            wasCreatedByMe: false,
        });
    };

    const handleRemove = () => {
        onChange({ imageUrl: null, imageAssetId: null });
    };

    return (
        <div>
            <div className={styles.imageHeaderRow}>
                <h6>{getText('msg_659e19b4591f')}</h6>
                <span
                    onClick={handleAddButtonClick}
                    role="button"
                    tabIndex={0}
                    className="button-base ing-button-primary-left"
                >
                    <i className="bi bi-plus-lg me-1" />
                    {getText('msg_61cc55aa0453')}
                </span>
                <Popover
                    id={addPopoverId}
                    open={addPopoverOpen}
                    anchorEl={anchorEl}
                    onClose={handlePopoverClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gap: 0,
                            paddingBottom: 0,
                            paddingTop: 0,
                            lineHeight: '40px',
                        }}
                    >
                        <div
                            onClick={handleAddFromLibrary}
                            role="button"
                            tabIndex={0}
                            className={`pointer-cursor profile-link ${styles.addPopoverOption}`}
                        >
                            {getText('msg_0bc609667aa7')}
                        </div>
                        <div
                            onClick={handleUploadFromDevice}
                            role="button"
                            tabIndex={0}
                            className={`pointer-cursor profile-link ${styles.addPopoverOption}`}
                        >
                            {getText('msg_c002fe6df29c')}
                        </div>
                    </Box>
                </Popover>
            </div>

            {currentUrl ? (
                <div className={styles.imagePreviewFrame}>
                    <button
                        type="button"
                        className={styles.imageRemoveBtn}
                        onClick={handleRemove}
                        aria-label={getText('msg_5f94b03c2e6a')}
                    >
                        <i className="bi bi-trash" />
                        <span>{getText('msg_f6fdbe48dc54')}</span>
                    </button>
                    <img src={currentUrl} alt={getText('msg_aee0d8dd793f')} />
                </div>
            ) : (
                <NotFoundBox
                    backgroundColor="#f0f4ff"
                    icon="asset"
                    title={getText('msg_4fed9fbe38b7')}
                    description={getText('msg_d59e487e098e')}
                />
            )}
        </div>
    );
};

PlanTypeImageTab.propTypes = {
    viewModel: PropTypes.object,
    draft: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    onUploadingChange: PropTypes.func,
};

export default PlanTypeImageTab;
